import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface AgentMetadata {
  name?: string
  description?: string
  image?: string
  active?: boolean
  services?: Array<{
    name?: string
    endpoint?: string
    skills?: string[]
    domains?: string[]
    additional_fields?: Record<string, unknown>
  }>
  x402Support?: boolean
  [key: string]: unknown
}

export interface Agent {
  agent_key: string
  chain_id: number
  registry_address: string
  token_id: string
  name: string | null
  description: string | null
  image_url: string | null
  metadata_uri: string | null
  metadata: AgentMetadata | null
  owner_address: string | null
  agent_wallet: string | null
  x402_support: 'true' | 'false' | 'unknown'
  availability: 'callable' | 'reachable' | 'experimental' | 'unknown'
  reputation_score: number | null
  reputation_count: number | null
  created_at: string | null
  updated_at: string | null
  registered_at: string | null
  registration_block: number | null
  endpoints: Endpoint[]
  // Domain verification (ERC-8004)
  domain_verified: boolean | null
  verified_endpoint: string | null
  verification_reason: string | null
}

export interface Endpoint {
  id: string
  agent_key: string
  protocol_type: 'mcp' | 'a2a' | 'oasf' | 'web' | 'wallet' | 'http_unknown'
  base_url: string
  version: string | null
  tools: string[]
  status: 'active' | 'inactive'
  probe_status?: string | null
}

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  agents?: Agent[]
  total?: number
  timestamp: Date
}

export interface Session {
  id: string
  title: string
  messages: ChatMessage[]
  createdAt: Date
}

interface SearchParams {
  q?: string
  protocol?: string
  online_only?: boolean
  callable_only?: boolean
  capability?: string
  limit: number
  offset?: number
}

interface ChatState {
  sessions: Session[]
  currentSessionId: string | null
  messages: ChatMessage[]
  searchResults: Agent[]
  searchTotal: number
  lastQuery: string
  lastSearchParams: SearchParams | null
  isLoading: boolean
  isLoadingMore: boolean

  // Session management
  createSession: () => void
  switchSession: (sessionId: string) => void
  deleteSession: (sessionId: string) => void
  goToWelcome: () => void

  // Message management
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void
  setSearchResults: (agents: Agent[], total: number, query: string, params: SearchParams) => void
  appendSearchResults: (agents: Agent[]) => void
  setLoading: (loading: boolean) => void
  setLoadingMore: (loading: boolean) => void
  clearMessages: () => void
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      sessions: [],
      currentSessionId: null,
      messages: [],
      searchResults: [],
      searchTotal: 0,
      lastQuery: '',
      lastSearchParams: null,
      isLoading: false,
      isLoadingMore: false,

      createSession: () => {
        const newSession: Session = {
          id: crypto.randomUUID(),
          title: '',
          messages: [],
          createdAt: new Date(),
        }
        set((state) => ({
          sessions: [newSession, ...state.sessions],
          currentSessionId: newSession.id,
          messages: [],
          searchResults: [],
          lastQuery: '',
        }))
      },

      switchSession: (sessionId) => {
        const session = get().sessions.find((s) => s.id === sessionId)
        if (session) {
          set({
            currentSessionId: sessionId,
            messages: session.messages,
            searchResults: [],
            lastQuery: '',
          })
        }
      },

      deleteSession: (sessionId) => {
        set((state) => {
          const newSessions = state.sessions.filter((s) => s.id !== sessionId)
          const needsSwitch = state.currentSessionId === sessionId
          return {
            sessions: newSessions,
            currentSessionId: needsSwitch ? (newSessions[0]?.id || null) : state.currentSessionId,
            messages: needsSwitch ? (newSessions[0]?.messages || []) : state.messages,
          }
        })
      },

      goToWelcome: () => {
        // Reset to welcome state without creating a session
        // A new session will be created automatically when the user searches
        set({
          currentSessionId: null,
          messages: [],
          searchResults: [],
          searchTotal: 0,
          lastQuery: '',
          lastSearchParams: null,
        })
      },

      addMessage: (message) => {
        const newMessage: ChatMessage = {
          ...message,
          id: crypto.randomUUID(),
          timestamp: new Date(),
        }

        set((state) => {
          // If no current session, create one
          let sessionId = state.currentSessionId
          let sessions = state.sessions

          if (!sessionId) {
            const newSession: Session = {
              id: crypto.randomUUID(),
              title: message.role === 'user' ? message.content.slice(0, 50) : '',
              messages: [],
              createdAt: new Date(),
            }
            sessions = [newSession, ...sessions]
            sessionId = newSession.id
          }

          // Update session with new message
          const newMessages = [...state.messages, newMessage]
          const updatedSessions = sessions.map((s) =>
            s.id === sessionId
              ? {
                  ...s,
                  messages: newMessages,
                  title: s.title || (message.role === 'user' ? message.content.slice(0, 50) : s.title),
                }
              : s
          )

          return {
            sessions: updatedSessions,
            currentSessionId: sessionId,
            messages: newMessages,
          }
        })
      },

      setSearchResults: (agents, total, query, params) =>
        set({
          searchResults: agents,
          searchTotal: total,
          lastQuery: query,
          lastSearchParams: params,
        }),

      appendSearchResults: (agents) =>
        set((state) => ({
          searchResults: [...state.searchResults, ...agents],
          lastSearchParams: state.lastSearchParams
            ? { ...state.lastSearchParams, offset: (state.lastSearchParams.offset || 0) + state.lastSearchParams.limit }
            : null,
        })),

      setLoading: (loading) =>
        set({ isLoading: loading }),

      setLoadingMore: (loading) =>
        set({ isLoadingMore: loading }),

      clearMessages: () =>
        set((state) => {
          if (!state.currentSessionId) return state

          const updatedSessions = state.sessions.map((s) =>
            s.id === state.currentSessionId
              ? { ...s, messages: [], title: '' }
              : s
          )

          return {
            sessions: updatedSessions,
            messages: [],
            searchResults: [],
            lastQuery: '',
          }
        }),
    }),
    {
      name: 'agent-discovery-chat',
      partialize: (state) => ({
        // Only persist sessions that have messages (don't save empty sessions)
        sessions: state.sessions.filter(s => s.messages.length > 0).slice(0, 20),
      }),
    }
  )
)
